import { routes } from "@/routes/routes";
import { OfferStatus } from "@/features/offer/types";
import { ChatStepId, type ChatStep, type OnboardingFacts } from "../types/chat";
import { chatCopy, formatIncome, maskCnp } from "./copy";

// The offer request is over, or the lender made a final offer, and nothing
// can be applied for: the user would be sent /application → /offer →
// /offer/not-available, so go there directly.
const hasNoUsableOffer = (facts: OnboardingFacts) =>
  !facts.hasAvailableOffer &&
  (facts.hasFinalOffer ||
    facts.offerRequestStatus === OfferStatus.Complete ||
    facts.offerRequestStatus === OfferStatus.NoOfferAvailable);

// The preliminary offer is behind the user: a final offer or a finished offer
// request. Users who started before the chat never gave income or a BC score,
// and don't need them any more.
const isPastPreliminaryOffer = (facts: OnboardingFacts) =>
  facts.hasFinalOffer ||
  facts.offerRequestStatus === OfferStatus.Complete ||
  facts.offerRequestStatus === OfferStatus.NoOfferAvailable;

const steps: readonly ChatStep[] = [
  {
    id: ChatStepId.ActiveLoan,
    needs: [],
    appliesWhen: (facts) => facts.hasActiveLoan,
    isDone: () => false,
    screen: { entry: () => routes.loan.base, group: ["/loan"] },
    copy: {
      prompt: () => ["Ai deja un credit activ la Volt."],
      cta: () => "Vezi creditul",
    },
  },
  {
    // Nothing else is asked once KYC is rejected: no offer could be activated
    // without it, and only support can validate it.
    id: ChatStepId.KycFailed,
    needs: [],
    appliesWhen: (facts) => facts.isKycFailed,
    isDone: () => false,
    screen: { entry: () => routes.kyc.failed, group: ["/kyc"] },
    copy: {
      prompt: () => ["Nu am putut confirma identitatea ta."],
      cta: () => "Contactează-ne",
    },
  },
  {
    id: ChatStepId.Cnp,
    needs: [],
    isDone: (facts) => facts.isCnpCompleted,
    screen: { entry: () => routes.onboarding.base, group: ["/onboarding"] },
    copy: {
      prompt: () => [...chatCopy.cnp],
      cta: () => "Introdu CNP-ul",
      done: "CNP salvat",
      answer: (profile) => (profile.cnp ? maskCnp(profile.cnp) : null),
    },
  },
  {
    // BC and ANAF together, right after the CNP, when the BC score comes
    // before KYC.
    id: ChatStepId.BcConsent,
    needs: [ChatStepId.Cnp],
    appliesWhen: (facts) => !isPastPreliminaryOffer(facts),
    isDone: (facts) => facts.isBcTermsAccepted,
    screen: { entry: () => routes.scoring.terms, group: ["/scoring/terms"] },
    copy: {
      prompt: () => [...chatCopy.bcConsent],
      cta: () => "Vezi acordul",
      done: "Acorduri acceptate",
      answer: () => "Da, sunt de acord",
    },
  },
  {
    id: ChatStepId.Email,
    needs: [],
    isDone: (facts) => facts.isEmailVerified,
    screen: { entry: () => routes.email.base, group: ["/email"] },
    copy: {
      prompt: (facts) =>
        facts.isEmailSaved && !facts.isEmailVerified
          ? [...chatCopy.emailCode]
          : [...chatCopy.email],
      cta: () => "Adaugă emailul",
      done: "Email confirmat",
      answer: (profile) => profile.email,
    },
  },
  {
    // Asked before the BC score: the backend freezes it into the score.
    id: ChatStepId.Income,
    needs: [],
    appliesWhen: (facts) => !isPastPreliminaryOffer(facts),
    isDone: (facts) => facts.hasDeclaredIncome,
    screen: { entry: () => routes.scoring.base, group: ["/scoring"] },
    copy: {
      prompt: () => [...chatCopy.income],
      cta: () => "Introdu venitul",
      done: "Venit declarat",
      answer: (profile) =>
        profile.declaredIncome ? formatIncome(profile.declaredIncome) : null,
    },
  },
  {
    id: ChatStepId.BcScoring,
    needs: [ChatStepId.Cnp, ChatStepId.Income],
    appliesWhen: (facts) => !isPastPreliminaryOffer(facts),
    isDone: (facts) =>
      facts.hasDeclaredIncome && facts.isBcTermsAccepted && facts.isScoringDone,
    // KYC when the backend cannot score by phone.
    screen: { entry: () => routes.scoring.base, group: ["/scoring", "/kyc"] },
    copy: {
      prompt: () => [...chatCopy.bcChecking],
      cta: () => "Continuă",
      done: "Scor verificat la Biroul de Credit",
    },
  },
  {
    // Shown until KYC is valid: the preliminary offer is activated through KYC.
    // Without a BC phone there is no score yet, and no offer to judge: KYC
    // comes first, then the BC scoring step runs again on it. Without a credit
    // history the turn opens the no-score screen without asking. With a final
    // offer already (the backend answers FinalOfferExists), KYC activates that
    // one instead.
    id: ChatStepId.PreliminaryOffer,
    needs: [ChatStepId.BcScoring],
    appliesWhen: (facts) => !facts.isKycValid && !isPastPreliminaryOffer(facts),
    isDone: () => false,
    screen: {
      entry: (facts) =>
        facts.hasBcScore
          ? routes.offer.preliminary
          : facts.isBcPhoneMissing
            ? routes.kyc.base
            : routes.offer.preliminaryNoScore,
      group: ["/offer/preliminary", "/kyc"],
    },
    // Only asked without a BC phone: otherwise the turn prices the offers, or
    // opens the no-score screen.
    copy: {
      prompt: () => [...chatCopy.bcNoPhones],
      cta: () => "Începe verificarea",
    },
  },
  {
    id: ChatStepId.Kyc,
    needs: [ChatStepId.Cnp],
    isDone: (facts) => facts.isKycValid,
    screen: { entry: () => routes.kyc.base, group: ["/kyc"] },
    copy: {
      prompt: () => [
        "Acum verificăm identitatea ta, rapid și în siguranță. Ține buletinul la îndemână.",
      ],
      cta: () => "Începe verificarea",
      done: "Identitate verificată",
    },
  },
  {
    // BC and ANAF consent together, after KYC.
    id: ChatStepId.Terms,
    needs: [ChatStepId.Kyc],
    isDone: (facts) => facts.isTermsAccepted,
    screen: { entry: () => routes.terms.base, group: ["/terms"] },
    copy: {
      prompt: () => [...chatCopy.terms],
      cta: () => "Vezi acordurile",
      done: "Acorduri acceptate",
      answer: () => "Da, sunt de acord",
    },
  },
  {
    // ANAF consent alone, when the lender could not record it with the BC
    // consent before KYC. No summary bubble: usually it was answered there.
    // A final offer means the lender already checked ANAF: users who applied
    // at Ocean directly have one, without the consent recorded at Volt.
    id: ChatStepId.AnafConsent,
    needs: [ChatStepId.Kyc],
    appliesWhen: (facts) => !facts.hasFinalOffer,
    isDone: (facts) => facts.isTermsAccepted,
    screen: { entry: () => routes.terms.anafConsent, group: ["/terms"] },
    copy: {
      prompt: () => [...chatCopy.anafConsent],
      cta: () => "Confirmă venitul",
      answer: () => "Da, sunt de acord",
    },
  },
  {
    id: ChatStepId.Questionnaire,
    needs: [],
    isDone: (facts) => facts.isQuestionnaireCompleted,
    screen: {
      entry: () => routes.questionnaire.base,
      group: ["/questionnaire"],
    },
    copy: {
      prompt: () => ["Încă două întrebări scurte despre ce îți dorești."],
      cta: () => "Răspunde",
      done: "Chestionar completat",
    },
  },
  {
    id: ChatStepId.ProofOfIncome,
    needs: [ChatStepId.Kyc],
    appliesWhen: (facts) => facts.isProofOfIncomeProcessing,
    isDone: () => false,
    screen: { entry: () => routes.offer.proofOfIncome, group: ["/offer"] },
    copy: {
      prompt: () => [
        "Verificăm dovada venitului tău. Te anunțăm imediat ce e gata.",
      ],
      cta: () => "Vezi starea",
    },
  },
  {
    // A returning user whose offers expired: both consents again, then a new
    // offer request.
    id: ChatStepId.OfferRenewal,
    needs: [ChatStepId.Kyc],
    appliesWhen: (facts) => facts.isOfferExpired,
    isDone: () => false,
    screen: { entry: () => routes.offer.base, group: ["/offer", "/terms"] },
    copy: {
      prompt: () => [...chatCopy.offerRenewal],
      cta: () => "Actualizează oferta",
    },
  },
  {
    // The offer request still needs something (terms, KYC) or is processing,
    // or was never made: consents given before KYC don't request it, the
    // offer screen does.
    id: ChatStepId.OfferRequest,
    needs: [ChatStepId.Kyc],
    appliesWhen: (facts) =>
      !facts.hasFinalOffer &&
      (facts.offerRequestStatus === null
        ? facts.isTermsAccepted
        : facts.offerRequestStatus !== OfferStatus.Complete),
    isDone: () => false,
    screen: {
      entry: () => routes.offer.base,
      group: ["/offer", "/kyc", "/terms"],
    },
    copy: {
      prompt: () => ["Pregătim oferta ta."],
      cta: () => "Vezi starea",
    },
  },
  {
    // Last step: choosing an offer, the calculator, card and signing stay screens.
    id: ChatStepId.Application,
    needs: [ChatStepId.Kyc],
    isDone: () => false,
    screen: {
      entry: (facts) =>
        hasNoUsableOffer(facts)
          ? routes.offer.notAvailable
          : routes.application.base,
      group: [
        "/application",
        "/offer",
        "/bank-card",
        "/loan",
        "/kyc",
        "/terms",
      ],
    },
    copy: {
      prompt: (facts) =>
        hasNoUsableOffer(facts)
          ? ["Momentan nu avem o ofertă pentru tine, dar avem câteva opțiuni."]
          : ["Oferta ta e gata! 🎉"],
      cta: (facts) =>
        hasNoUsableOffer(facts) ? "Vezi opțiunile" : "Vezi oferta",
    },
  },
];

const stepsById = new Map<string, ChatStep>(
  steps.map((step) => [step.id, step]),
);

export const isChatStepId = (value: string): value is ChatStepId =>
  stepsById.has(value);

export const getChatStep = (id: ChatStepId): ChatStep => stepsById.get(id)!;
